import React, { useRef } from 'react';
import { styles } from './ProductBannerCard.css';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { ProgressiveImage } from '../../hooks/useProgressiveImage/useProgressiveImage';
import { AlgoliaHitTransformer } from '../../Algolia/transformers/AlgoliaHitTransformer';
import { HitPriceTag } from '../../ProductCompare/HitPriceTag/HitPriceTag';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import { getVariantImageSize } from '../utils';
import BannerTag from '@jbhi-fi/lanyard-ui/components/BannerTag';
import { BannerBadge } from '../../BannerBadge';
import { JBBonusGiftCard } from '../../BannerBadge/JBBonusGiftCard';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { Link, useNavigate } from '../../PlatformFunctionality';
import { pixelsToRem } from '@jbhi-fi/lanyard-ui/themes';
import { formatPrice } from '@jbhi-fi/lanyard-ui/global/utils';
import { TrackingData, trackingDataToDataAttributes } from '../../HeroBanner/utils';
import { useTrackingContext } from '../../Tracking';

type ProductBannerCardProps = {
  hit: AlgoliaProductHit;
  variant: 'small' | 'medium' | 'large' | 'mobile';
  endDate?: string;
  tagType: 'Banner tag' | 'Badge';
  badgeStyle?: 'Red flag' | 'Star burst';
  enableEAPriceBox?: boolean;
  badge?: '$ off' | '% off' | '1/2 price' | 'While stocks last!' | 'Hot price!' | 'Hot deal!';
  trackingData?: TrackingData;
};
export const ProductBannerCard = ({
  hit,
  variant,
  endDate,
  tagType,
  badge,
  badgeStyle,
  enableEAPriceBox,
  trackingData,
}: ProductBannerCardProps) => {
  const { product_image, title, brand_image_url, vendor } = hit;
  const isMobileVariant = variant === 'mobile';
  const isSmallVariant = variant === 'small';
  const isMediumVariant = variant === 'medium';
  const isLargeVariant = variant === 'large';
  const hasBadge = !!badge;
  const bannerLabel = hit.banner_tags?.label;
  const shouldShowGiftCard = hit?.all_calculated_product_tags?.some(t => t.toLowerCase().includes('jb gift card'));
  const saveAmount = Number(formatPrice(Math.abs(Number(hit.pricing.wasPrice) - Number(hit.pricing.displayPriceInc))));
  const isPricingBadge = ['$ off', '% off', '1/2 price'].includes(badge ?? '');
  const hideBadgeRHD = isPricingBadge && hit?.priceBannerTag?.label?.toLowerCase() === 'red hot deal';
  const hasLongTitle = title.length > 65;
  const navigateTo = useNavigate();
  const { tracker } = useTrackingContext();
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const hasDeliveryTag = !!hit.deliveryTag;
  const showEndDateWithoutBadge = isMobileVariant && !hasBadge && tagType === 'Badge';

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = () => {
    tracker.trackCustomEvent({ event: 'internal promotion click', gaipElement: anchorRef.current });
  };

  return (
    <Link
      ref={anchorRef}
      href={`/products/${hit.handle}`}
      onClick={handleClick}
      {...trackingDataToDataAttributes(
        trackingData ? { ...trackingData, category: trackingData.category || tracker.category } : undefined
      )}
      style={{ textDecoration: 'none', width: '100%' }}
    >
      <div className={styles.container({ variant })}>
        {tagType === 'Badge' && hasBadge && isLargeVariant && !hideBadgeRHD ? (
          <div className={styles.badgeContainerLarge}>
            <BannerBadge
              badgeStyle={badgeStyle}
              badge={badge}
              saveAmount={saveAmount}
              wasPrice={hit.pricing.wasPrice ?? 0}
              displayWasPrice={hit.pricing.displayWasPrice}
              variant="large"
              endDate={endDate}
              displayPrice={hit.pricing.displayPriceInc}
            />
          </div>
        ) : isLargeVariant && !hasBadge && tagType === 'Badge' && endDate ? (
          <div className={styles.bannerTagDateContainer}>
            <div className={styles.endDateContainer}>
              <CalloutFont className={styles.endDateContainerText}>Ends {endDate}</CalloutFont>
            </div>
          </div>
        ) : tagType === 'Banner tag' && isLargeVariant ? (
          <div className={styles.bannerTagDateContainer}>
            {bannerLabel ? <BannerTag testId="product-card-banner-tag" size="lg" label={bannerLabel} /> : null}
            {endDate ? (
              <div className={styles.endDateContainer}>
                <CalloutFont className={styles.endDateText({ variant })}>Ends {endDate}</CalloutFont>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className={styles.imageBannerBadgeContainer}>
          {tagType === 'Badge' && hasBadge && !isLargeVariant && !hideBadgeRHD ? (
            <div className={styles.badgeContainerSmall({ variant })}>
              <BannerBadge
                badgeStyle={badgeStyle}
                badge={badge}
                saveAmount={saveAmount}
                wasPrice={hit.pricing.wasPrice ?? 0}
                displayWasPrice={hit.pricing.displayWasPrice}
                variant={isMobileVariant ? 'mobile' : 'small'}
                endDate={endDate}
                displayPrice={hit.pricing.displayPriceInc}
              />
            </div>
          ) : null}
          {tagType === 'Banner tag' && !isLargeVariant ? (
            <div style={{ minHeight: '24px' }}>
              {bannerLabel ? <BannerTag testId="product-card-banner-tag" size="sm" label={bannerLabel} /> : null}
            </div>
          ) : null}

          <ProgressiveImage
            className={styles.image}
            {...AlgoliaHitTransformer.toProgressiveImageProps(
              { product_image, title },
              { sizePx: getVariantImageSize(variant) }
            )}
          />
          {isLargeVariant && shouldShowGiftCard ? (
            <div className={styles.bonusGiftCardContainer({ isLargeVariant })}>
              <JBBonusGiftCard variant="large" />
            </div>
          ) : null}
        </div>
        <div className={styles.titlePriceDateContainer({ variant })}>
          <div className={styles.brandTitleContainer({ isMobileVariant })}>
            <div className={styles.brandLogoContainer}>
              {brand_image_url ? <img className={styles.brandLogo} src={brand_image_url} alt={vendor} /> : null}
            </div>
            <div
              className={styles.productTitle({ isMobileVariant })}
              style={
                isMobileVariant
                  ? { fontSize: pixelsToRem(hasLongTitle ? 12 : 14) }
                  : isLargeVariant
                    ? { fontSize: pixelsToRem(hasLongTitle ? 17 : 20) }
                    : { fontSize: pixelsToRem(hasLongTitle ? 14 : 16) }
              }
            >
              {title}
            </div>
          </div>
          <div className={styles.bottomRightContainer({ isLargeVariant })}>
            <div className={styles.priceTagContainer({ variant })}>
              <HitPriceTag
                hit={hit}
                variant={isMobileVariant ? 'extraSmall' : isSmallVariant ? 'small' : 'default'}
                className={styles.priceTag}
                showDelivery
                showEA={enableEAPriceBox}
              />
            </div>
            {(!isLargeVariant && !isMobileVariant) ||
            (isMobileVariant && tagType === 'Banner tag') ||
            showEndDateWithoutBadge ? (
              <div
                className={styles.endDateText({
                  variant,
                  isFreeDeliveryMobile: hasDeliveryTag && isMobileVariant,
                  isFreeDeliverySmall: hasDeliveryTag && isSmallVariant,
                  isFreeDeliveryMedium: hasDeliveryTag && isMediumVariant,
                })}
              >
                {endDate ? (
                  <>
                    <b>Ends: </b>
                    <p className={styles.endDateTextWrap}>{endDate}</p>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          {isLargeVariant || isMobileVariant ? (
            <div className={styles.buttonContainer({ isMobileVariant })}>
              <JBButton
                onClick={() => {
                  navigateTo(`/products/${hit.handle}`);
                }}
                type="primary"
                label="Shop now"
                fullWidth
                className={styles.button({ isLargeVariant })}
              />
            </div>
          ) : null}
        </div>
        {shouldShowGiftCard && !isLargeVariant ? (
          <div className={styles.bonusGiftCardContainer({ isMobileVariant, isSmallVariant })}>
            <JBBonusGiftCard variant={isMobileVariant ? 'mobile' : 'small'} />
          </div>
        ) : null}
      </div>
    </Link>
  );
};
